import { useState, useMemo } from 'react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  useConfirmDialog,
} from '@/components/ui';
import { CampoBuilderDialog } from './CampoBuilderDialog';
import {
  ENTIDADES_SUPORTADAS,
  TIPOS_DISPONIVEIS,
  useAdminCustomFields,
  useAdminCustomFieldsActions,
  type AdminCustomField,
  type ScopeTipo,
} from './store';

/**
 * /admin/campos-personalizados (A6.4).
 *
 * Lista + filtros (cadastro, onde vale, ativos) + ações (criar/editar/
 * desativar/duplicar). Cada item mostra o nome humano do campo e, como
 * detalhe secundário, o identificador técnico.
 *
 * Wizard de criação/edição em `CampoBuilderDialog`.
 *
 * NÃO REVERTER:
 *   - Filtro padrão `apenasAtivos=true` — desativados ficam ocultos por default
 *   - Toggle "Mostrar também os desativados" mostra todos
 *   - DELETE é soft (deactivateField backend) — mensagem explicita
 *   - Listagem ordenada por (ordem ASC, label ASC) já no backend
 */

/** Onde o campo vale — em português, sem "scope". */
const ESCOPO_LABEL: Record<ScopeTipo, string> = {
  global: 'Vale em todo o sistema',
  workflow: 'Só num fluxo de trabalho',
  modal: 'Só numa janela',
  pagina: 'Só numa tela',
};

export function CamposPersonalizadosListPage() {
  const [entidadeFilter, setEntidadeFilter] = useState<string>('');
  const [scopeFilter, setScopeFilter] = useState<ScopeTipo | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState<{ mode: 'create' | 'edit'; field?: AdminCustomField } | null>(null);

  const { data: allFields = [], isLoading } = useAdminCustomFields({
    entidade: entidadeFilter || undefined,
    scopeTipo: scopeFilter || undefined,
    apenasAtivos: !includeInactive,
  });

  const filteredFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allFields;
    return allFields.filter(
      (f) =>
        f.label.toLowerCase().includes(q) ||
        f.key.toLowerCase().includes(q) ||
        (f.descricao ?? '').toLowerCase().includes(q),
    );
  }, [allFields, search]);

  const { deactivate, duplicate } = useAdminCustomFieldsActions();
  const confirm = useConfirmDialog();

  async function handleDeactivate(field: AdminCustomField) {
    const ok = await confirm({
      title: 'Desativar campo?',
      message: `O campo "${field.label}" para de aparecer nos formulários. O que já foi preenchido continua guardado, e dá pra reativar depois.`,
      confirmLabel: 'Desativar',
      variant: 'destructive',
    });
    if (!ok) return;
    deactivate.mutate(field.id);
  }

  function handleDuplicate(field: AdminCustomField) {
    // Duplicação simples — clona pro mesmo escopo com key_copia
    duplicate.mutate({
      id: field.id,
      scopeTipo: field.scopeTipo,
      scopeId: field.scopeId,
      key: `${field.key}_copia`,
    });
  }

  return (
    <div className="min-w-0">
      <PageHeader
        crumb="Configurações · Sistema"
        title="Campos personalizados"
        subtitle="Crie campos que não vêm de fábrica — em negócios, clientes, projetos e outros cadastros — e escolha quem enxerga cada um."
        actions={<Button onClick={() => setOpenDialog({ mode: 'create' })}>Novo campo</Button>}
      />

      {/* Filtros */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <Label htmlFor="entidade-filter">Cadastro</Label>
            <Select
              id="entidade-filter"
              value={entidadeFilter}
              onChange={(e) => setEntidadeFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {ENTIDADES_SUPORTADAS.map((ent) => (
                <option key={ent.value} value={ent.value}>
                  {ent.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-0">
            <Label htmlFor="scope-filter">Onde vale</Label>
            <Select
              id="scope-filter"
              value={scopeFilter}
              onChange={(e) => setScopeFilter((e.target.value || '') as ScopeTipo | '')}
            >
              <option value="">Em qualquer lugar</option>
              <option value="global">Em todo o sistema</option>
              <option value="workflow">Num fluxo de trabalho</option>
              <option value="modal">Numa janela</option>
              <option value="pagina">Numa tela</option>
            </Select>
          </div>
          <div className="min-w-0">
            <Label htmlFor="search-filter">Buscar</Label>
            <Input
              id="search-filter"
              type="search"
              placeholder="Nome do campo ou descrição"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex min-w-0 items-end">
            <Checkbox
              label="Mostrar também os desativados"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
          </div>
        </div>
      </Card>

      {/* Listagem */}
      {isLoading && (
        <div className="py-6 text-center text-sm text-ink-3 dark:text-ink-dark-3">
          Carregando campos…
        </div>
      )}

      {!isLoading && filteredFields.length === 0 && (
        <EmptyState
          title="Nenhum campo personalizado"
          description={
            search || entidadeFilter || scopeFilter
              ? 'Ajuste os filtros pra ver outros campos, ou crie um novo.'
              : 'Comece criando o primeiro campo em “Novo campo”.'
          }
          action={<Button onClick={() => setOpenDialog({ mode: 'create' })}>Novo campo</Button>}
        />
      )}

      <div className="grid grid-cols-1 gap-3">
        {filteredFields.map((f) => (
          <Card key={f.id} className={`p-4 ${f.ativo === 0 ? 'opacity-60' : ''}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">
                    {f.label}
                  </h2>
                  {f.ativo === 0 && (
                    <Badge variant="neutral" size="sm">
                      Desativado
                    </Badge>
                  )}
                  {f.computedExpression && (
                    <Badge variant="info" size="sm">
                      Calculado
                    </Badge>
                  )}
                  {/* K-02 — quem preenche este campo é gente de fora. O aviso
                      existe no construtor, mas quem varre a lista atrás de
                      "onde vazou esse dado" precisa ver na listagem, sem abrir
                      campo por campo. */}
                  {f.mostrarNoFormPublico === 1 && (
                    <Badge variant="warning" size="sm" data-testid={`campo-publico-${f.id}`}>
                      Formulário público
                    </Badge>
                  )}
                  {/* K-02 — cadastro sem tela que desenhe campo personalizado:
                      o campo existe, mas ninguém tem por onde preencher. */}
                  {entidadeSemTela(f.entidade) && (
                    <Badge variant="neutral" size="sm" data-testid={`campo-sem-tela-${f.id}`}>
                      Sem tela
                    </Badge>
                  )}
                </div>
                <div className="mb-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-3 dark:text-ink-dark-3">
                  <span>{tipoLabel(f.tipo)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{entidadeLabel(f.entidade)}</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    {ESCOPO_LABEL[f.scopeTipo] ?? f.scopeTipo}
                    {f.scopeId ? ` (${f.scopeId})` : ''}
                  </span>
                  {f.grupo && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>Grupo: {f.grupo}</span>
                    </>
                  )}
                </div>
                {f.descricao && (
                  <p className="line-clamp-2 text-sm leading-snug text-ink-2 dark:text-ink-dark-2">
                    {f.descricao}
                  </p>
                )}
                <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
                  Identificador:{' '}
                  <code className="break-words rounded bg-paper-warm px-1.5 py-0.5 font-mono dark:bg-paper-dark-warm">
                    {f.key}
                  </code>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenDialog({ mode: 'edit', field: f })}
                >
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDuplicate(f)}
                  disabled={duplicate.isPending}
                >
                  Duplicar
                </Button>
                {f.ativo === 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeactivate(f)}
                    disabled={deactivate.isPending}
                  >
                    Desativar
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {openDialog && (
        <CampoBuilderDialog
          mode={openDialog.mode}
          field={openDialog.field}
          onClose={() => setOpenDialog(null)}
        />
      )}
    </div>
  );
}

function tipoLabel(tipo: AdminCustomField['tipo']): string {
  const t = TIPOS_DISPONIVEIS.find((x) => x.value === tipo);
  return t ? t.label : tipo;
}

function entidadeLabel(entidade: string): string {
  const e = ENTIDADES_SUPORTADAS.find((x) => x.value === entidade);
  return e ? e.label : entidade;
}

/**
 * Cadastro sem nenhuma tela que desenhe campos personalizados.
 *
 * Entidade DESCONHECIDA (nem está na lista) também conta: o banco aceita
 * `entidade` livre, então dá pra ter campo de um cadastro que ninguém mais
 * reconhece — e esse é o caso mais órfão de todos.
 */
function entidadeSemTela(entidade: string): boolean {
  const e = ENTIDADES_SUPORTADAS.find((x) => x.value === entidade);
  return !e || e.onde === null;
}
